import React from 'react';
import { CreditCard, Plus, Eye, EyeOff, Lock, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mockCards, formatCurrency } from '@/utils/mockData';

export default function CardsPage() {
  const [showDetails, setShowDetails] = React.useState({});

  return (
    <div className="max-w-6xl mx-auto" data-testid="cards-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-heading text-slate-900">My Cards</h1>
          <p className="text-slate-600 mt-1">Manage your virtual and physical cards</p>
        </div>
        <Button data-testid="create-card-btn" className="bg-brand-blue text-white hover:bg-blue-700 rounded-full px-6 shadow-lg transition-all active:scale-95">
          <Plus className="w-5 h-5 mr-2" />
          Create Card
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {mockCards.map((card) => (
          <div key={card.id} data-testid={`card-${card.id}`} className="relative">
            <div className={`rounded-3xl p-8 shadow-2xl text-white relative overflow-hidden ${card.type === 'virtual' ? 'bg-gradient-to-br from-purple-600 to-purple-800' : 'bg-gradient-to-br from-slate-800 to-slate-900'}`}>
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/4 translate-x-1/4"></div>
              <div className="relative z-10 space-y-8">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white/80 text-sm mb-1 capitalize">{card.type} Card</p>
                    <p className="text-2xl font-bold">Britedge</p>
                  </div>
                  <img src={`https://placehold.co/60x40/white/white?text=${card.brand}`} alt={card.brand} className="h-10" />
                </div>
                
                <div>
                  <p className="text-white/80 text-sm mb-2">Card Number</p>
                  <div className="flex items-center gap-4">
                    <p className="text-2xl font-mono tracking-wider">
                      {showDetails[card.id] ? `**** **** **** ${card.last4}` : '**** **** **** ****'}
                    </p>
                    <button onClick={() => setShowDetails({...showDetails, [card.id]: !showDetails[card.id]})} data-testid={`toggle-card-${card.id}`} className="text-white/60 hover:text-white transition-colors">
                      {showDetails[card.id] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-white/80 text-xs mb-1">Expires</p>
                    <p className="text-lg font-mono">{card.expiryDate}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/80 text-xs mb-1">Balance</p>
                    <p className="text-xl font-bold">{formatCurrency(card.balance)}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex gap-3">
              <Button size="sm" data-testid={`fund-card-${card.id}`} className="flex-1 bg-brand-blue text-white hover:bg-blue-700 rounded-xl transition-all active:scale-95">
                Fund Card
              </Button>
              <Button size="sm" variant="outline" data-testid={`freeze-card-${card.id}`} className="rounded-xl">
                <Lock className="w-4 h-4 mr-2" />
                Freeze
              </Button>
              <Button size="sm" variant="outline" data-testid={`delete-card-${card.id}`} className="rounded-xl text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="text-lg font-bold font-heading text-slate-900 mb-4">Recent Card Transactions</h3>
        <div className="space-y-3">
          {[
            { merchant: 'Jumia Nigeria', amount: 15000, date: 'Dec 20, 2024', card: '4521' },
            { merchant: 'Uber Trip', amount: 3500, date: 'Dec 19, 2024', card: '8934' },
            { merchant: 'Netflix Subscription', amount: 4500, date: 'Dec 18, 2024', card: '4521' }
          ].map((txn, idx) => (
            <div key={idx} className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{txn.merchant}</p>
                  <p className="text-sm text-slate-600">{txn.date} • Card ending {txn.card}</p>
                </div>
              </div>
              <p className="font-bold text-red-600">-{formatCurrency(txn.amount)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
